(() => {
  if (window.ProMobileProductGallery) return;

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const init = (gallery) => {
    if (!gallery?.matches?.('[data-pro-mobile-product-gallery]')) return null;
    if (instances.has(gallery)) return instances.get(gallery);

    const viewport = gallery.querySelector('[data-pro-mobile-gallery-viewport]');
    const slides = Array.from(gallery.querySelectorAll('[data-pro-mobile-slide]'));
    if (!viewport || !slides.length) return null;

    const thumbsRail = gallery.querySelector('[data-pro-mobile-thumbs]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-pro-mobile-thumb]'));
    const controller = new AbortController();
    const { signal } = controller;

    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let scrollFrame = 0;
    let scrollEndTimer = 0;
    let resizeFrame = 0;
    let resizeObserver = null;
    let programmaticTarget = null;

    const wrapIndex = (index) => {
      if (index < 0) return slides.length - 1;
      if (index >= slides.length) return 0;
      return index;
    };

    const nearestIndex = () => {
      const width = viewport.clientWidth || 1;
      return Math.min(slides.length - 1, Math.max(0, Math.round(viewport.scrollLeft / width)));
    };

    const pauseSlideMedia = (slide) => {
      if (!slide) return;

      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      slide.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      slide.querySelectorAll('product-model').forEach((model) => {
        model.modelViewerUI?.pause?.();
      });
    };

    const centerActiveThumb = (animate = false) => {
      const thumb = thumbs[current];
      if (!thumb || !thumbsRail) return;

      const targetLeft = Math.max(
        0,
        thumb.offsetLeft - (thumbsRail.clientWidth - thumb.offsetWidth) / 2,
      );

      thumbsRail.scrollTo({
        left: targetLeft,
        behavior: animate && !reducedMotion.matches ? 'smooth' : 'auto',
      });
    };

    const updateActiveState = ({ centerThumb = false, animateThumb = false } = {}) => {
      slides.forEach((slide, index) => {
        const active = index === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (!active) pauseSlideMedia(slide);
      });

      thumbs.forEach((thumb, index) => {
        const active = index === current;
        thumb.classList.toggle('is-active', active);

        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      if (centerThumb) centerActiveThumb(animateThumb);
    };

    const jumpToCurrent = () => {
      viewport.scrollLeft = slides[current]?.offsetLeft || current * viewport.clientWidth;
    };

    const scrollToIndex = (index, animate = true) => {
      const next = wrapIndex(index);
      const wrapped =
        (current === 0 && next === slides.length - 1) ||
        (current === slides.length - 1 && next === 0);

      current = next;
      programmaticTarget = next;
      updateActiveState({ centerThumb: true, animateThumb: animate });

      viewport.scrollTo({
        left: slides[next]?.offsetLeft || next * viewport.clientWidth,
        behavior: animate && !wrapped && !reducedMotion.matches ? 'smooth' : 'auto',
      });
    };

    const setActiveMedia = (mediaId, animate = true) => {
      if (mediaId === null || mediaId === undefined || mediaId === '') return false;

      const requestedId = String(mediaId);
      const requestedSuffix = `-${requestedId}`;
      const index = slides.findIndex((slide) => {
        const slideMediaId = slide.dataset.mediaId || '';
        return slideMediaId === requestedId || slideMediaId.endsWith(requestedSuffix);
      });

      if (index < 0) return false;

      scrollToIndex(index, animate);
      return true;
    };

    const settleScroll = () => {
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = 0;

      current = programmaticTarget ?? nearestIndex();
      programmaticTarget = null;
      updateActiveState({ centerThumb: true, animateThumb: false });
    };

    const onScroll = () => {
      if (!scrollFrame) {
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;

          if (programmaticTarget === null) {
            const next = nearestIndex();
            if (next !== current) {
              current = next;
              updateActiveState();
            }
          }
        });
      }

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(settleScroll, 120);
    };

    gallery.addEventListener(
      'click',
      (event) => {
        const thumb = event.target.closest('[data-pro-mobile-thumb]');
        if (thumb && gallery.contains(thumb)) {
          scrollToIndex(Number.parseInt(thumb.dataset.proMobileIndex || '0', 10) || 0, true);
          return;
        }

        if (event.target.closest('[data-pro-mobile-prev]')) {
          scrollToIndex(current - 1, true);
          return;
        }

        if (event.target.closest('[data-pro-mobile-next]')) {
          scrollToIndex(current + 1, true);
        }
      },
      { signal },
    );

    viewport.addEventListener('scroll', onScroll, { passive: true, signal });

    viewport.addEventListener(
      'pointerdown',
      () => {
        programmaticTarget = null;
      },
      { passive: true, signal },
    );

    if ('onscrollend' in viewport) {
      viewport.addEventListener('scrollend', settleScroll, { passive: true, signal });
    }

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          jumpToCurrent();
          updateActiveState();
        });
      });
      resizeObserver.observe(viewport);
    }

    const destroy = () => {
      controller.abort();
      resizeObserver?.disconnect();
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(resizeFrame);
      window.clearTimeout(scrollEndTimer);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      instances.delete(gallery);
    };

    const onSectionUnload = (event) => {
      if (!event.target?.contains(gallery)) return;
      destroy();
    };

    const api = {
      controller,
      destroy,
      setActiveMedia,
    };

    document.addEventListener('shopify:section:unload', onSectionUnload);

    updateActiveState();
    window.requestAnimationFrame(jumpToCurrent);

    instances.set(gallery, api);
    return api;
  };

  const mediaSequence = (gallery) =>
    Array.from(gallery?.querySelectorAll?.('[data-pro-mobile-slide]') || [], (slide) => slide.dataset.mediaId || '');

  const sync = (gallery, sourceGallery) => {
    if (!gallery?.matches?.('[data-pro-mobile-product-gallery]')) return null;

    const currentSequence = mediaSequence(gallery);
    const sourceSequence = mediaSequence(sourceGallery);
    const mediaListChanged =
      sourceSequence.length > 0 &&
      (currentSequence.length !== sourceSequence.length ||
        currentSequence.some((mediaId, index) => mediaId !== sourceSequence[index]));

    if (mediaListChanged) {
      instances.get(gallery)?.destroy();
      gallery.replaceChildren(...Array.from(sourceGallery.childNodes, (node) => node.cloneNode(true)));
    }

    return init(gallery);
  };

  const setActiveMedia = (gallery, mediaId, animate = true) =>
    (instances.get(gallery) || init(gallery))?.setActiveMedia(mediaId, animate) || false;

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-pro-mobile-product-gallery]')) init(scope);
    scope.querySelectorAll?.('[data-pro-mobile-product-gallery]').forEach(init);
  };

  window.ProMobileProductGallery = {
    init,
    initAll,
    sync,
    setActiveMedia,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
